import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import type { VolumeSettings } from '@repo/api-gw-sdk';

import {
  convertFromBytes,
  convertToBytes,
  fileSizeFormatter,
} from '@/utils/fileSizeFormatter';

import { getValueInRange, SliderWithInput } from './sliderWithInput';

import { Loader } from '../layout/loading';

export const volumePresets: Record<
  string,
  {
    displayName: string;
    minSize: number;
    maxSize: number;
    minIOPS?: number;
    maxIOPS?: number;
    minThroughput?: number;
    maxThroughput?: number;
  }
> = {
  gp3: {
    displayName: 'General Purpose SSD (gp3)',
    minSize: 1,
    maxSize: 16384,
    minIOPS: 3000,
    maxIOPS: 16000,
    minThroughput: 125,
    maxThroughput: 1000,
  },
  // IOPS is auto-calculated according to size, but it's not editable
  // Throughput is not applicable
  gp2: {
    displayName: 'General Purpose SSD (gp2)',
    minSize: 1,
    maxSize: 16384,
  },
  io1: {
    // IOPS max value changes according to size
    // Throughput is not applicable
    displayName: 'Provisioned IOPS SSD (io1)',
    minSize: 4,
    maxSize: 16384,
    minIOPS: 100,
    maxIOPS: 400,
  },
  io2: {
    // IOPS max value changes according to size
    // Throughput is not applicable
    displayName: 'Provisioned IOPS SSD (io2)',
    minSize: 4,
    maxSize: 65536,
    minIOPS: 100,
    maxIOPS: 400,
  },
  sc1: {
    // IOPS is not applicable
    // Throughput is auto-calculated according to size, but it's not editable
    displayName: 'Cold HDD (sc1)',
    minSize: 125,
    maxSize: 16384,
  },
  st1: {
    // IOPS is not applicable
    // Throughput is auto-calculated according to size, but it's not editable
    displayName: 'Throughput Optimized HDD (st1)',
    minSize: 125,
    maxSize: 16384,
  },
  standard: {
    // IOPS is not applicable
    // Throughput is not applicable
    displayName: 'Magnetic (standard)',
    minSize: 1,
    maxSize: 1024,
  },
};

interface Props {
  initialSettings?: VolumeSettings;
  settings?: VolumeSettings;
  onChange: (params: VolumeSettings) => void;
  keepOriginalSettings?: boolean;
  setKeepOriginalSettings: (value: boolean) => void;
}

export const VolumeSettingsSection = ({
  initialSettings,
  onChange,
  settings: _settings,
  keepOriginalSettings,
  setKeepOriginalSettings,
}: Props) => {
  if (!initialSettings) {
    return <Loader />;
  }
  const settings = _settings || initialSettings;
  const preset = volumePresets[settings.type] || volumePresets.gp3;

  return (
    <Stack className='w-full px-[40px]' gap='24px'>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography className='font-semibold' variant='body1'>
          Keep original settings
        </Typography>
        <Switch
          size='small'
          checked={keepOriginalSettings}
          onChange={() => {
            setKeepOriginalSettings(!keepOriginalSettings);
          }}
        />
      </Stack>
      {keepOriginalSettings && (
        <Box>
          <Table size='small'>
            <TableBody>
              <TableRow>
                <TableCell className='border-0 px-0 w-[300px]'>Type</TableCell>
                <TableCell className='border-0 px-0'>
                  {volumePresets[initialSettings.type].displayName}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-0 px-0 w-[300px]'>Size</TableCell>
                <TableCell className='border-0 px-0'>
                  {fileSizeFormatter(initialSettings.sizeBytes, 1, false)}
                </TableCell>
              </TableRow>
              {initialSettings.iops && (
                <TableRow>
                  <TableCell className='border-0 px-0 w-[300px]'>
                    IOPS
                  </TableCell>
                  <TableCell className='border-0 px-0'>
                    {initialSettings.iops}
                  </TableCell>
                </TableRow>
              )}
              {initialSettings.throughput && (
                <TableRow>
                  <TableCell className='border-0 px-0 w-[300px]'>
                    Throughput
                  </TableCell>
                  <TableCell className='border-0 px-0'>{`${initialSettings.throughput} MB/s`}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}
      {!keepOriginalSettings && (
        <Box className='w-1/2 pr-[18px]'>
          <FormControl size='small' className='w-full mb-[24px]'>
            <FormLabel>Volume Type</FormLabel>
            <Select
              value={settings.type}
              onChange={(e) => {
                const newType = e.target.value;
                const newPreset = volumePresets[newType];

                onChange({
                  type: newType,
                  sizeBytes: settings.sizeBytes,
                  iops:
                    newPreset.minIOPS && newPreset.maxIOPS
                      ? getValueInRange(
                          settings.iops || initialSettings.iops,
                          newPreset.minIOPS,
                          newPreset.maxIOPS
                        )
                      : undefined,
                  throughput:
                    newPreset.minThroughput && newPreset.maxThroughput
                      ? getValueInRange(
                          settings.throughput || initialSettings.throughput,
                          newPreset.minThroughput,
                          newPreset.maxThroughput
                        )
                      : undefined,
                });
              }}
            >
              {Object.entries(volumePresets).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size='small' className='w-full mb-[24px]'>
            <SliderWithInput
              id='size'
              title='Size (GiB)'
              value={convertFromBytes(settings.sizeBytes, 'GiB')}
              minValue={convertFromBytes(initialSettings.sizeBytes, 'GiB')}
              maxValue={preset.maxSize}
              onChange={(value) =>
                onChange({
                  ...settings,
                  sizeBytes: convertToBytes(value, 'GiB'),
                })
              }
            />
          </FormControl>
          {preset.minIOPS && preset.maxIOPS && (
            <FormControl size='small' className='w-full mb-[24px]'>
              <SliderWithInput
                id='iops'
                title='IOPS'
                value={settings.iops}
                minValue={preset.minIOPS}
                maxValue={preset.maxIOPS}
                onChange={(value) => onChange({ ...settings, iops: value })}
              />
            </FormControl>
          )}
          {preset.minThroughput && preset.maxThroughput && (
            <FormControl size='small' className='w-full mb-[24px]'>
              <SliderWithInput
                id='throughput'
                title='Throughput (MB/s)'
                value={settings.throughput}
                minValue={preset.minThroughput}
                maxValue={preset.maxThroughput}
                onChange={(value) =>
                  onChange({ ...settings, throughput: value })
                }
              />
            </FormControl>
          )}
        </Box>
      )}
    </Stack>
  );
};

// FSx ONTAP Volume Settings
interface FsxVolumeSettings {
  sizeBytes: number;
  storageEfficiencyEnabled: boolean;
}

interface FsxVolumeSettingsProps {
  initialSettings: FsxVolumeSettings;
  settings?: FsxVolumeSettings;
  onChange: (params: FsxVolumeSettings) => void;
  keepOriginalSettings: boolean;
  setKeepOriginalSettings: (value: boolean) => void;
}

export const FsxVolumeSettingsSection = ({
  initialSettings,
  onChange,
  settings: _settings,
  keepOriginalSettings,
  setKeepOriginalSettings,
}: FsxVolumeSettingsProps) => {
  const settings = _settings || initialSettings;

  const handleToggle = () => {
    const newKeepOriginal = !keepOriginalSettings;
    setKeepOriginalSettings(newKeepOriginal);
    // Reset to original settings when toggling on
    if (newKeepOriginal) {
      onChange(initialSettings);
    }
  };

  return (
    <Stack className='w-full px-[40px]' gap='24px'>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography className='font-semibold' variant='body1'>
          Keep original settings
        </Typography>
        <Switch
          size='small'
          checked={keepOriginalSettings}
          onChange={handleToggle}
        />
      </Stack>
      {keepOriginalSettings && (
        <Box>
          <Table size='small'>
            <TableBody>
              <TableRow>
                <TableCell className='border-0 px-0 w-[300px]'>Size</TableCell>
                <TableCell className='border-0 px-0'>
                  {fileSizeFormatter(initialSettings.sizeBytes, 1, false)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className='border-0 px-0 w-[300px]'>
                  Deduplication and compression
                </TableCell>
                <TableCell className='border-0 px-0'>
                  {initialSettings.storageEfficiencyEnabled
                    ? 'Enabled'
                    : 'Disabled'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}
      {!keepOriginalSettings && (
        <Box className='w-1/2 pr-[18px]'>
          <FormControl size='small' className='w-full mb-[24px]'>
            <SliderWithInput
              id='size'
              title='Size (GiB)'
              value={Math.ceil(convertFromBytes(settings.sizeBytes, 'GiB'))}
              minValue={Math.ceil(
                convertFromBytes(initialSettings.sizeBytes, 'GiB')
              )}
              maxValue={1048576} // 1 PiB max for ONTAP volumes
              onChange={(value) =>
                onChange({
                  ...settings,
                  sizeBytes: convertToBytes(value, 'GiB'),
                })
              }
            />
          </FormControl>
          <FormControl size='small' className='w-full mb-[24px]'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={settings.storageEfficiencyEnabled}
                  onChange={(event) =>
                    onChange({
                      ...settings,
                      storageEfficiencyEnabled: event.target.checked,
                    })
                  }
                />
              }
              label='Enable deduplication and compression'
            />
          </FormControl>
        </Box>
      )}
    </Stack>
  );
};
